import type { AuthenticatedUser } from '@ottd/api';

/*
 * The public SPA only needs one permission question: may this user open the
 * admin panel? Names mirror the backend RolesAndPermissionsSeeder and the
 * admin app's own gate (web/admin/src/lib/permissions.ts PANEL_ACCESS). UX
 * affordance only - the admin app and the server re-check on entry.
 */

const ROLE_ROOT = 'root';

/** Anyone who may open the admin panel at all (all three seeded roles). */
const PANEL_ACCESS = ['show-admin', 'all-admin'] as const;

/*
 * TEMPORARY: moderators are locked out of the panel until their view is
 * reworked (backend EnsureAdminPanelAccess, admin SPA isLockedOutOfPanel), so
 * the menu hides its link for them. A moderator who is also admin gets in as
 * that role; root short-circuits before this is checked.
 */
const LOCKED_OUT_ROLE = 'moderator';
const ROLE_ADMIN = 'admin';

/** Same-origin admin SPA (served under /admin by the reverse proxy). */
export const ADMIN_PANEL_URL = '/admin/';

/**
 * True when the user may open the admin panel: `root` short-circuits (it has
 * an unconditional Gate::before bypass server-side), everyone else needs one
 * of the panel-access permissions and must not be a locked-out moderator.
 */
export function canAccessAdminPanel(user: AuthenticatedUser | null): boolean {
  if (user === null) return false;
  if (user.roles.includes(ROLE_ROOT)) return true;
  if (user.roles.includes(LOCKED_OUT_ROLE) && !user.roles.includes(ROLE_ADMIN)) return false;
  return PANEL_ACCESS.some((permission) => user.permissions.includes(permission));
}
